import React, { useContext, useEffect } from "react";
import { AxiosError } from "axios";
import { useForm } from "@mantine/form";
import { Space } from "@mantine/core";
import { Navigate } from "react-router-dom";
import useCreateTagCategoryApiService from "../services/api/CreateTagCategoryApiService";
import { AuthContext } from "../../../providers/AuthProvider";
import { HttpClientError } from "../../../providers/HttpClient";
import { queryClient } from "../../../App";
import TagCategorySelect from "../elements/TagCategorySelect";
import { useToasts } from "../../../providers/notifications/ToastsContext";
import TagNameInput from "./TagNameInput";
import TagFormActions from "./TagFormActions";

type TagFormProps = {
  onSuccess?: () => void;
  onFailure?: () => void;
};

const TagForm: React.FC<TagFormProps> = ({ onSuccess, onFailure }) => {
  const auth = useContext(AuthContext);
  const { showToast } = useToasts();

  const {
    isLoading: isCreateLoading,
    isSuccess: isCreateSuccess,
    isError: isCreateFailure,
    error: createError,
    mutate: onCreate,
  } = useCreateTagCategoryApiService();

  const failureNotification = (err: AxiosError | null): void =>
    showToast({
      strapiId: "tag_submission_failed",
      data: {
        message: HttpClientError(err),
      },
    });

  useEffect(() => {
    if (isCreateFailure) {
      failureNotification(createError);
      if (onFailure) {
        onFailure();
      }
    }
  }, [isCreateFailure, createError, onFailure]);

  useEffect(() => {
    if (isCreateSuccess) {
      queryClient
        .invalidateQueries(["TagsCategoryListApiService", ""])
        .then(() => {
          if (onSuccess) {
            onSuccess();
          }
          showToast({ strapiId: "tags_category_created" });
        });
    }
  }, [isCreateSuccess]);

  if (!auth) {
    return <Navigate replace to="/login" />;
  }

  const form = useForm({
    initialValues: {
      id: 0,
      category: "",
      name: "",
      employees: [],
    },
  });

  const onSubmitHandler = (formValues: typeof form.values): void => {
    onCreate({
      ...formValues,
      id: undefined,
      employer: auth.employer.id,
      source: "manual",
    });
  };

  return (
    <form
      onSubmit={form.onSubmit((formValues) => onSubmitHandler(formValues))}
      style={{ width: "100%" }}
    >
      <TagNameInput form={form} />
      <Space h="xl" />
      <TagCategorySelect form={form} />
      <Space h="xl" />
      <TagFormActions isLoading={isCreateLoading} />
    </form>
  );
};

export default TagForm;
